import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getMhdProfile } from "src/apis/controllers/mhdProfile";
import {
  ApartmentCard,
  BeneficiaryFilter,
  ExpenseSummary,
  ProfileHeader,
} from "./ProfileComponents";
import StaffSection from "./StaffSection";
import { Visibility } from "@mui/icons-material";

const MhdProfile = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [beneficiaryFilter, setBeneficiaryFilter] = useState("current");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getMhdProfile(applicationId);
      if (response?.succeeded === false || !response?.data) {
        setError(response?.message || "Δεν βρέθηκαν στοιχεία για τη ΜΗΔ.");
        return;
      }
      setProfile(response.data);
    } catch (requestError) {
      setError(requestError?.message || "Δεν φορτώθηκαν τα στοιχεία της ΜΗΔ.");
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  if (loading) {
    return (
      <Box sx={{ minHeight: 320, display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !profile) {
    return <Alert severity="error" sx={{ m: 3 }}>{error || "Δεν βρέθηκε η ΜΗΔ."}</Alert>;
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, maxWidth: 1600, mx: "auto" }}>
      <ProfileHeader profile={profile} onBack={() => navigate("/govnr/apartments")} />
      <Stack spacing={1.5}>
        <Paper variant="outlined" sx={{ p: { xs: 1.25, md: 1.75 } }}>
          <StaffSection profile={profile} onRefresh={loadProfile} />
        </Paper>

        <Paper component="section" variant="outlined" sx={{ p: { xs: 1.25, md: 1.75 } }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 1.25 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography component="h2" variant="h4" fontWeight={700}>Διαμερίσματα ({profile.apartments?.length || ""}) και ωφελούμενοι</Typography>
            </Stack>
            <BeneficiaryFilter value={beneficiaryFilter} onChange={setBeneficiaryFilter} />
          </Stack>
          {profile.apartments?.length ? (
            <Stack spacing={1}>
              {profile.apartments.map((apartment) => (
                <ApartmentCard
                  key={apartment.id}
                  apartment={apartment}
                  applicationId={profile.applicationId}
                  mhdName={profile.name}
                  filter={beneficiaryFilter}
                />
              ))}
            </Stack>
          ) : <Alert severity="info">Δεν υπάρχουν διαμερίσματα για αυτή τη ΜΗΔ.</Alert>}
        </Paper>

        {profile.canViewExpenses && profile.expenses && (
          <Paper component="section" variant="outlined" sx={{ p: { xs: 1.25, md: 1.75 } }}>
            <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "flex-start", sm: "center" }} justifyContent="space-between" spacing={1}>
              <Typography component="h2" variant="h4" fontWeight={700}>Συγκεντρωτικά δαπανών</Typography>
              <Button variant="contained" startIcon={<Visibility />} size="small" onClick={() => navigate(`/expenses/${profile.applicationId}`)}>
                Προβολή αναλυτικών δαπανών
              </Button>
            </Stack>
            <ExpenseSummary summary={profile.expenses} showHeader={false} />
          </Paper>
        )}
      </Stack>
    </Box>

  );
};

export default MhdProfile;
